import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { Col, Divider, Drawer, Input, Row } from "antd";
import { styled, css } from "styled-components";
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from "swiper/react";
import { Pagination, Mousewheel, Navigation } from "swiper/modules";
import "swiper/css/virtual";
import { Virtual } from "swiper/modules";
// Import Swiper styles
import "swiper/css";
import { fontSize, fontsWeight } from "@/shahrzad-assets/shared-constants/fonts";
import { dimens } from "@/shahrzad-assets/shared-constants/dimens";
import { colors } from "@/shahrzad-assets/shared-constants/colors";
import CustomButton from "@/shahrzad-assets/shared_components/CustomButton";
import { buttonTypes } from "@/shahrzad-assets/shared-constants/buttonTypes";
import { icons } from "@/shahrzad-assets/shared-asstes/icons";
import { CText } from "@/shahrzad-assets/shared_components/CText";
import dayjs from "dayjs";

interface IProps {
  title?: string;
  onChange: (value: string) => void;
  colorInput?: string;
  customIcon?: string;
  hasDefaultValue?: boolean;
  placeHolder?: string;
  label?: string;
}

const MobileDatePicker = ({
  title = "انتخاب تاریخ تولد",
  onChange,
  hasDefaultValue = true,
  placeHolder,
  colorInput,
  customIcon,
  label,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const initialDay = dayjs().format("DD");
  const initialMonth = dayjs().format("MM");
  const initialYear = dayjs().format("YYYY");

  const [day, setDay] = useState(initialDay);
  const [month, setMonth] = useState(initialMonth);
  const [year, setYear] = useState<string>(initialYear);
  const selectedDate = useMemo(
    () =>
      `${year}/${month.length === 1 ? "0" + month : month}/${
        day.length === 1 ? "0" + day : day
      }`,
    [year, month, day]
  );
  const [value, setValue] = useState(hasDefaultValue ? selectedDate : "");

  const swiperDayRef = useRef<SwiperRef>(null);
  const swiperMonthRef = useRef<SwiperRef>(null);
  const swiperYearRef = useRef<SwiperRef>(null);

  const days = Array.from(
    { length: 31 },
    (value, index) => `${index + 1}`
  ) as string[];

  const months = Array.from(
    { length: 12 },
    (value, index) => `${index + 1}`
  ) as string[];

  const years = Array.from(
    { length: 120 },
    (value, index) => dayjs().subtract(index, "y").format("YYYY") as string
  ) as string[];

  const onSlide = (ref: RefObject<SwiperRef>, index: string | number) => {
    ref?.current?.swiper.slideTo(parseInt(index));
  };

  const onChangeSlider =
    (list: string[], setState: React.Dispatch<React.SetStateAction<string>>) =>
    (swiper: SwiperClass) => {
      setState(list[swiper.activeIndex]);
    };

  const onDaySwiper = () => {
    onSlide(swiperDayRef, parseInt(day) - 1);
  };

  const onMonthSwiper = () => {
    onSlide(swiperMonthRef, parseInt(month) - 1);
  };

  const onYearSwiper = () => {
    onSlide(swiperYearRef, 0);
  };

  const onOpenCalendar = () => {
    setOpen(true);
  };

  const onCloseCalendar = () => {
    setOpen(false);
  };

  const onChangeDateInput = () => {
    onChange?.(value);
  };

  const onSaveDateValue = () => {
    setValue(selectedDate);
    onCloseCalendar();
  };

  useEffect(() => {
    onChangeDateInput();
  }, [value]);

  const onDelete = () => {
    setDay(initialDay);
    setMonth(initialMonth);
    setYear(initialYear);
    onSlide(swiperDayRef, initialDay);
    onSlide(swiperMonthRef, initialMonth);
    onSlide(swiperYearRef, 0);
  };

  const swiperProps = {
    grabCursor: true,
    direction: "vertical",
    slidesPerView: 3,
    centeredSlides: true,
    mousewheel: true,
    pagination: true,
    navigation: true,
    modules: [Mousewheel, Virtual, Pagination, Navigation],
    virtual: true,
    loop: false,
  };

  return (
    <Container className="fit-width">
      {label && <label htmlFor="birthdate">{label}</label>}

      <DatepickerInput
        id="birthdate"
        placeholder={placeHolder}
        value={value}
        onChange={onChangeDateInput}
        onClick={onOpenCalendar}
        suffix={<img src={customIcon} />}
        color={colorInput}
      />
      <Drawer
        className="fit-width"
        title=""
        placement={"bottom"}
        closable={false}
        onClose={onCloseCalendar}
        open={open}
        height={300}
      >
        <DrawerHeader>
          <h4>{title}</h4>
          <img onClick={onCloseCalendar} src={icons.CLOSE_BLACK} />
        </DrawerHeader>
        <Divider />
        <DatePickerContainer>
          <Header>
            <div>روز</div>
            <div>ماه</div>
            <div>سال</div>
          </Header>
          <SwiperContainer>
            <CSwiper
              onSwiper={onDaySwiper}
              ref={swiperDayRef}
              onSlideChange={onChangeSlider(days, setDay)}
              {...swiperProps}
            >
              {days.map((d, index) => {
                return (
                  <CSwiperSlide border="right" virtualIndex={index} key={d}>
                    {" "}
                    <Date>{d}</Date>
                  </CSwiperSlide>
                );
              })}
            </CSwiper>

            <CSwiper
              ref={swiperMonthRef}
              onSwiper={onMonthSwiper}
              onSlideChange={onChangeSlider(months, setMonth)}
              {...swiperProps}
            >
              {months.map((d, index) => {
                return (
                  <CSwiperSlide virtualIndex={index} key={d}>
                    <Date>{d}</Date>
                  </CSwiperSlide>
                );
              })}
            </CSwiper>
            {years.length > 0 && (
              <CSwiper
                ref={swiperYearRef}
                onSwiper={onYearSwiper}
                onSlideChange={onChangeSlider(years, setYear)}
                {...swiperProps}
              >
                {years?.map((d, index) => {
                  return (
                    <CSwiperSlide border="left" virtualIndex={index} key={d}>
                      {" "}
                      <Date>{d}</Date>
                    </CSwiperSlide>
                  );
                })}
              </CSwiper>
            )}
          </SwiperContainer>
        </DatePickerContainer>
        <DatePickerButtons gutter={12}>
          <Col span={12}>
            <CustomButton
              onClick={onSaveDateValue}
              styletype={buttonTypes.PRIMARY_FILL}
              width="100%"
            >
              {"تایید"}
            </CustomButton>
          </Col>
          <Col span={12}>
            <CustomButton
              onClick={onCloseCalendar}
              styletype={buttonTypes.PRIMARY_OUTLINE}
              width="100%"
            >
              {"انصراف"}
            </CustomButton>
          </Col>
        </DatePickerButtons>
      </Drawer>
    </Container>
  );
};

export default MobileDatePicker;

const Container = styled.div`
  label {
    display: block;
    color: ${colors.text3};
    font-size: ${fontSize.SMALL};
    transition: all 300ms ease-out;
    cursor: text;
    text-align: right;
    margin-bottom: ${dimens.SPACING_XXS};
  }
`;
const DatePickerContainer = styled.div`
  overflow: hidden;
  max-height: ${dimens.DRAWER_BOTTOM_HEIGHT};
  height: 100%;
`;
const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${dimens.SPACING_M};

  h4 {
    font-size: ${fontSize.MEDIUM};
  }
  img {
    width: 1.2rem;
    cursor: pointer;
  }
`;

const Header = styled.div`
  display: flex;
  text-align: center;
  & > div {
    flex-basis: calc(100% / 3);
  }
`;

const SwiperContainer = styled.div`
  display: flex;
  height: 90%;
  text-align: center;
  & > div {
    flex-basis: calc(100% / 3);
  }
`;

const Date = styled.p`
  font-size: ${fontSize.SMALL};
  font-weight: ${fontsWeight.BOLD};
`;

const CSwiper = styled(Swiper)<any>`
  margin: 0 !important ;
  .swiper-slide-active {
    background-color: ${colors.bg3};
  }
`;

const CSwiperSlide = styled(SwiperSlide)<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.border === "right" &&
    css`
      border-radius: 0 ${dimens.BORDER_RADIUS_8} ${dimens.BORDER_RADIUS_8} 0;
    `}
  ${(props) =>
    props.border === "left" &&
    css`
      border-radius: ${dimens.BORDER_RADIUS_8} 0 0 ${dimens.BORDER_RADIUS_8};
    `}
`;

const ClearText = styled(CText)`
  position: absolute;
  bottom: ${dimens.SPACING_S};
  cursor: pointer;
`;
const DatepickerInput = styled(Input)`
  width: 100%;
  height: 3rem;
  border-radius: ${dimens.BORDER_RADIUS_8};
  border: 1px solid ${colors.stroke2};
  .ant-input {
    &::placeholder {
      color: ${colors.text3} !important;
      opacity: 1 !important;
      font-size: ${fontSize.SMALL} !important;
    }
  }
  ${(props) =>
    props.color &&
    css`
      background: ${props.color};
      color: ${colors.WHITE};
      border: none;
      .ant-input {
        color: ${colors.WHITE} !important;
        background: ${props.color};
      }
    `}
`;

const DatePickerButtons = styled(Row)`
  margin-top: ${dimens.SPACING_XXXL};
`;

CSwiperSlide.displayName = "SwiperSlide";
